package co.uk.paxes.pinpoint.engine

import co.uk.paxes.pinpoint.model.*
import co.uk.paxes.pinpoint.DebugLogger
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch

class GameEngine(
    val persistence: Any? = null,
    val aiSpeed: Any? = null,
    val listener: GameEngineListener? = null
) {
    private val scope = CoroutineScope(Dispatchers.Main + SupervisorJob())
    private var state: GameState? = null
    private val scoreCalculator = ScoreCalculator()

    fun startGame(config: GameConfig) {
        // Initialize game state with proper board seed for dress pins
        // If restoring, use the existing board. Otherwise create new one.
        val board = if (config.existingBoard != null) {
            DebugLogger.i("GameEngine", "Using existing board state (restore mode)")
            config.existingBoard
        } else if (config.boardSeed != null) {
            Board(initializeDressPins = !config.isNetworkClient, randomSeed = config.boardSeed)
        } else {
            Board(initializeDressPins = !config.isNetworkClient)
        }
        
        DebugLogger.i("GameEngine", "Creating board with seed=${config.boardSeed}, isNetworkClient=${config.isNetworkClient}, restoring=${config.existingBoard != null}")
        
        val players = config.players
        val startingPlayerId = config.startingPlayerId ?: players.firstOrNull()?.id
        val startingIndex = players.indexOfFirst { it.id == startingPlayerId }.takeIf { it != -1 } ?: 0
        
        // Generate initial hands for all players
        val playerHands = players.associate { player ->
            player.id to generateNewHand()
        }
        
        val initialHand = playerHands[startingPlayerId] ?: generateNewHand()

        state = GameState(
            gameId = "game_${kotlin.random.Random.nextLong()}",
            board = board,
            players = players,
            currentPlayerIndex = startingIndex,
            gamePhase = GamePhase.PLAYING,
            currentHand = initialHand,
            playerHands = playerHands,
            handCount = 0,
            started = true,
            currentPlayerId = startingPlayerId
        )
        
        DebugLogger.i("GameEngine", "Game started with ${players.size} players, boardSeed=${config.boardSeed}")
        listener?.onGameStarted(state!!)
    }

    fun getCurrentGameState(): GameState? {
        return state
    }

    /**
     * Restore a saved board state into the current game.
     * Used for game restore functionality on PlayGama.
     */
    fun restoreBoardState(savedBoard: Board) {
        val currentState = state ?: return
        state = currentState.copy(board = savedBoard)
        DebugLogger.i("GameEngine", "Board state restored with ${savedBoard.getOccupiedPositions().size} pins")
    }

    private fun generateNewHand(): List<Pin> {
        // Standard hand always contains exactly one of each color
        return listOf(
            Pin(PinColor.RED),
            Pin(PinColor.GREEN),
            Pin(PinColor.PURPLE),
            Pin(PinColor.YELLOW)
        )
    }

    fun applyLocalMove(move: Move) {
        val currentState = state ?: return
        
        // DEBUG: Log hand before move
        println("🎯 applyLocalMove: BEFORE - hand size=${currentState.currentHand.size}, pins=${currentState.currentHand.map { it.color }}")
        
        // Validation
        if (!currentState.board.isValidMove(move.position)) {
            DebugLogger.d("GameEngine", "Invalid move at ${move.position}")
            return
        }

        // Apply move to board FIRST (like Android does)
        var newBoard = currentState.board.placePinAt(move.position, move.pin)
        
        // Calculate Score on the board WITH the new pin placed (like Android's calculateScore)
        // This is critical for detecting 6+ pin lines that include the newly placed pin
        val scoreResult = scoreCalculator.calculateMoveScore(newBoard, move)
        
        // Handle reversals
        if (scoreResult.pinsToReverse.isNotEmpty()) {
            newBoard = newBoard.reversePinsAt(scoreResult.pinsToReverse)
        }
        
        // Update Player Score AND joker count if joker was used
        val currentPlayerId = currentState.currentPlayerId ?: move.playerId
        val currentPlayer = currentState.players.find { it.id == currentPlayerId }
        val updatedPlayers = if (currentPlayer != null) {
            val newScore = currentPlayer.score + scoreResult.points
            currentState.players.map {
                if (it.id == currentPlayerId) {
                    val playerWithScore = it.copy(score = newScore)
                    // Increment joker count if this was a joker move
                    if (move.pin.isJoker) playerWithScore.useJoker() else playerWithScore
                } else it
            }
        } else currentState.players

        // Update Hand (Remove played pin)
        val currentHand = currentState.playerHands[currentPlayerId] ?: currentState.currentHand
        val newHand = currentHand.toMutableList()
        
        // Find pin to remove (match color)
        // Note: move.pin might be a joker, so we use originalColor if present
        // CRITICAL FIX: When a joker is played, we need to remove the REGULAR pin with the original color
        // (not a joker pin with that color, since the hand contains regular pins)
        val colorToMatch = move.pin.originalColor ?: move.pin.color
        val pinToRemove = if (move.pin.isJoker) {
            // For jokers, find a REGULAR pin with the original color
            newHand.find { it.color == colorToMatch && !it.isJoker }
        } else {
            // For regular pins, find an exact match
            newHand.find { it.color == colorToMatch && it.isJoker == move.pin.isJoker }
        } ?: newHand.firstOrNull() // Fallback
            
        if (pinToRemove != null) {
            newHand.remove(pinToRemove)
        }

        // Check for Hand Refill and Turn Switch
        var nextPlayerIndex = currentState.currentPlayerIndex
        var nextPlayerId = currentState.currentPlayerId ?: currentState.players[nextPlayerIndex].id
        val updatedPlayerHands = currentState.playerHands.toMutableMap()
        var nextHand: List<Pin>
        
        if (newHand.isEmpty()) {
            // Hand complete: 
            // 1. Refill the current player's hand for their NEXT turn
            // 2. Switch to next player
            // 3. Generate a new hand for the next player (this becomes currentHand)
            DebugLogger.d("GameEngine", "Hand complete! Switching turn from player $nextPlayerIndex")
            
            // Refill current player's hand for their next turn
            if (currentPlayerId != null) {
                updatedPlayerHands[currentPlayerId] = generateNewHand()
            }
            
            // Switch to next player
            nextPlayerIndex = (currentState.currentPlayerIndex + 1) % currentState.players.size
            nextPlayerId = currentState.players[nextPlayerIndex].id
            
            // Generate hand for next player (or use existing if they have one)
            nextHand = updatedPlayerHands[nextPlayerId] ?: generateNewHand()
            updatedPlayerHands[nextPlayerId] = nextHand
            
            // Turn jokers black on board
            newBoard = newBoard.turnJokersBlack()
            
            DebugLogger.d("GameEngine", "Turn switched to player $nextPlayerIndex ($nextPlayerId), hand size: ${nextHand.size}")
        } else {
            // Hand NOT complete: same player continues
            nextHand = newHand.toList()
            if (currentPlayerId != null) {
                updatedPlayerHands[currentPlayerId] = nextHand
            }
        }

        // Update State
        state = currentState.copy(
            board = newBoard,
            players = updatedPlayers,
            moves = currentState.moves + move,
            currentPlayerIndex = nextPlayerIndex,
            currentPlayerId = nextPlayerId,
            playerHands = updatedPlayerHands,
            currentHand = nextHand
        )
        
        // DEBUG: Log hand after move
        println("🎯 applyLocalMove: AFTER - hand size=${nextHand.size}, pins=${nextHand.map { it.color }}")
        
        // Notify listener
        listener?.onMoveApplied(state!!, move)
    }

    /**
     * Apply a remote move from another player.
     * This mirrors Android's forceMakeMove logic - it handles turn switching based on moveNumber.
     * 
     * CRITICAL: The move's playerId determines whose turn it is, not currentPlayerIndex.
     * This is essential for HOST receiving CLIENT moves - the HOST's currentPlayerIndex
     * might be wrong, but the move's playerId is always correct.
     * 
     * @param move The move to apply
     * @param moveNumber The move number (1-based). moveNumber % 4 == 0 means hand complete.
     *                   If 0, falls back to hand size tracking.
     */
    fun applyRemoteMove(move: Move, moveNumber: Int = 0) {
        val currentState = state ?: return
        
        // DEBUG: Log hand before remote move
        println("🏠 applyRemoteMove: BEFORE - hand size=${currentState.currentHand.size}, moveNumber=$moveNumber")
        
        // Validation
        if (!currentState.board.isValidMove(move.position)) {
            DebugLogger.d("GameEngine", "Invalid remote move at ${move.position}")
            return
        }

        // Apply move to board FIRST (like Android does)
        var newBoard = currentState.board.placePinAt(move.position, move.pin)
        
        // Calculate Score on the board WITH the new pin placed (like Android's calculateScore)
        // This is critical for detecting 6+ pin lines that include the newly placed pin
        val scoreResult = scoreCalculator.calculateMoveScore(newBoard, move)
        
        // Handle reversals
        if (scoreResult.pinsToReverse.isNotEmpty()) {
            newBoard = newBoard.reversePinsAt(scoreResult.pinsToReverse)
        }
        
        // Update Player Score AND joker count if joker was used
        val movePlayerId = move.playerId
        val movePlayer = currentState.players.find { it.id == movePlayerId }
        val updatedPlayers = if (movePlayer != null) {
            val newScore = movePlayer.score + scoreResult.points
            currentState.players.map {
                if (it.id == movePlayerId) {
                    val playerWithScore = it.copy(score = newScore)
                    // Increment joker count if this was a joker move
                    if (move.pin.isJoker) playerWithScore.useJoker() else playerWithScore
                } else it
            }
        } else currentState.players

        // CRITICAL: Find the player index for the move's player
        // This is essential because the HOST's currentPlayerIndex might be wrong
        // when receiving a CLIENT's move
        val movePlayerIndex = currentState.players.indexOfFirst { it.id == movePlayerId }
        if (movePlayerIndex < 0) {
            DebugLogger.d("GameEngine", "applyRemoteMove: Player $movePlayerId not found in players list")
            return
        }

        // CRITICAL: Handle turn switching like Android's forceMakeMove
        // Android removes a pin from currentHand to track hand depletion
        // Even though HOST doesn't know CLIENT's exact hand, it tracks the COUNT
        
        // Remove a pin from currentHand (like Android's forceMakeMove)
        val newHand = currentState.currentHand.toMutableList()
        
        // Try to find a matching pin first (for better accuracy when possible)
        val matchingPinIndex = if (move.pin.isJoker) {
            // For jokers, find a regular pin with the original color
            newHand.indexOfFirst { it.color == move.pin.originalColor && !it.isJoker }
        } else {
            // For regular pins, find an exact match first
            val exactMatch = newHand.indexOfFirst { it == move.pin }
            if (exactMatch >= 0) {
                exactMatch
            } else {
                // Fallback: find any pin with the same color
                newHand.indexOfFirst { it.color == move.pin.color && !it.isJoker }
            }
        }
        
        if (matchingPinIndex >= 0) {
            newHand.removeAt(matchingPinIndex)
            DebugLogger.d("GameEngine", "applyRemoteMove: Removed matching pin at index $matchingPinIndex, hand size now ${newHand.size}")
        } else if (newHand.isNotEmpty()) {
            // CRITICAL: Even if we can't find the exact pin, we MUST remove a pin to track hand completion
            // This is essential for internet multiplayer where host doesn't know client's hand
            DebugLogger.d("GameEngine", "applyRemoteMove: No matching pin found for ${move.pin.color} (joker=${move.pin.isJoker}), removing first pin to maintain hand count")
            newHand.removeAt(0)
        } else {
            DebugLogger.d("GameEngine", "applyRemoteMove: Hand is already empty!")
        }
        
        // Determine hand completion using BOTH moveNumber AND hand size (like Android's forceMakeMove)
        val isHandCompleteByMoveNumber = moveNumber > 0 && moveNumber % 4 == 0
        val isHandCompleteByHandSize = newHand.isEmpty()
        val isHandComplete = isHandCompleteByMoveNumber || isHandCompleteByHandSize
        
        DebugLogger.d("GameEngine", "applyRemoteMove: moveNumber=$moveNumber, handSize=${newHand.size}, isHandComplete=$isHandComplete (byMoveNum=$isHandCompleteByMoveNumber, byHandSize=$isHandCompleteByHandSize)")
        
        // Handle turn switching
        val nextPlayerIndex: Int
        val nextPlayerId: String
        val nextHandForState: List<Pin>
        val finalBoard: Board
        val updatedPlayerHands = currentState.playerHands.toMutableMap()
        
        if (isHandComplete) {
            DebugLogger.d("GameEngine", "applyRemoteMove: Hand complete! Switching turn from player $movePlayerIndex")
            
            // Refill move player's hand for their next turn
            if (movePlayerId.isNotEmpty()) {
                updatedPlayerHands[movePlayerId] = generateNewHand()
            }
            
            // Switch to next player (from the move player, not currentPlayerIndex)
            nextPlayerIndex = (movePlayerIndex + 1) % currentState.players.size
            nextPlayerId = currentState.players[nextPlayerIndex].id
            
            // Generate hand for next player
            nextHandForState = updatedPlayerHands[nextPlayerId] ?: generateNewHand()
            updatedPlayerHands[nextPlayerId] = nextHandForState
            
            // Turn jokers black
            finalBoard = newBoard.turnJokersBlack()
            
            DebugLogger.d("GameEngine", "applyRemoteMove: Turn switched to player $nextPlayerIndex ($nextPlayerId)")
        } else {
            // Same player continues - use the MOVE player's index, not currentPlayerIndex
            nextPlayerIndex = movePlayerIndex
            nextPlayerId = movePlayerId
            // Use the depleted hand (with pin removed)
            nextHandForState = newHand.toList()
            // Update playerHands map with depleted hand
            if (movePlayerId.isNotEmpty()) {
                updatedPlayerHands[movePlayerId] = nextHandForState
            }
            finalBoard = newBoard
        }

        // Update State with turn switching
        state = currentState.copy(
            board = finalBoard,
            players = updatedPlayers,
            moves = currentState.moves + move,
            currentPlayerIndex = nextPlayerIndex,
            currentPlayerId = nextPlayerId,
            currentHand = nextHandForState,
            playerHands = updatedPlayerHands
        )
        
        // DEBUG: Log hand after remote move
        println("🏠 applyRemoteMove: AFTER - hand size=${nextHandForState.size}, nextPlayerId=$nextPlayerId")
        
        // Notify listener
        listener?.onMoveApplied(state!!, move)
    }

    fun syncTurnState(playerId: String) {
        // Sync turn state - update both currentPlayerId and currentPlayerIndex
        val currentState = state ?: return
        if (currentState.currentPlayerId != playerId) {
            // Find the player index for this playerId
            val playerIndex = currentState.players.indexOfFirst { it.id == playerId }
            if (playerIndex >= 0) {
                state = currentState.copy(
                    currentPlayerId = playerId,
                    currentPlayerIndex = playerIndex
                )
            } else {
                // Fallback: just update currentPlayerId
                state = currentState.copy(currentPlayerId = playerId)
            }
        }
    }

    fun updateBoardState(board: Board) {
        val currentState = state ?: return
        
        // Sync moves list with board state to handle Undos correctly
        // We assume board has 4 dress pins, so actual moves = total pins - 4
        // If we have fewer pins than moves+4, it means moves were undone
        val pinCount = board.getOccupiedPositions().size
        val impliedMoveCount = if (pinCount >= 4) pinCount - 4 else 0
        
        var newMoves = currentState.moves
        if (newMoves.size > impliedMoveCount) {
             DebugLogger.d("GameEngine", "Syncing moves list: reducing from ${newMoves.size} to $impliedMoveCount")
             newMoves = newMoves.take(impliedMoveCount)
        }
        
        state = currentState.copy(board = board, moves = newMoves)
    }

    fun updatePlayerScores(scores: Map<String, Int>) {
        val currentState = state ?: return
        val updatedPlayers = currentState.players.map { player ->
            if (scores.containsKey(player.id)) {
                player.copy(score = scores[player.id]!!)
            } else {
                player
            }
        }
        state = currentState.copy(players = updatedPlayers)
    }

    fun updatePlayerJokers(jokersMap: Map<String, Int>) {
        val currentState = state ?: return
        val updatedPlayers = currentState.players.map { player ->
            if (jokersMap.containsKey(player.id)) {
                player.copy(jokersUsed = jokersMap[player.id]!!)
            } else {
                player
            }
        }
        state = currentState.copy(players = updatedPlayers)
    }

    fun updateCurrentHand(hand: List<Pin>) {
        val currentState = state ?: return
        // CRITICAL: Also update playerHands to keep them in sync
        // This is important for undo scenarios where the hand is restored
        val currentPlayerId = currentState.currentPlayerId
        val updatedPlayerHands = if (currentPlayerId != null) {
            currentState.playerHands.toMutableMap().apply {
                this[currentPlayerId] = hand
            }
        } else {
            currentState.playerHands
        }
        state = currentState.copy(currentHand = hand, playerHands = updatedPlayerHands)
    }
    
    /**
     * Update all player hands from a map.
     * This is used when syncing state from the host in web-to-web multiplayer.
     */
    fun updatePlayerHands(hands: Map<String, List<Pin>>) {
        val currentState = state ?: return
        val updatedPlayerHands = currentState.playerHands.toMutableMap()
        hands.forEach { (playerId, hand) ->
            updatedPlayerHands[playerId] = hand
        }
        state = currentState.copy(playerHands = updatedPlayerHands)
    }
    
    /**
     * Update the current player index and ID.
     * This is used when syncing state from the host.
     */
    fun updateCurrentPlayerIndex(index: Int) {
        val currentState = state ?: return
        val newPlayerId = currentState.players.getOrNull(index)?.id
        state = currentState.copy(
            currentPlayerIndex = index,
            currentPlayerId = newPlayerId
        )
    }
    
    /**
     * Update the hand count.
     */
    fun updateHandCount(count: Int) {
        val currentState = state ?: return
        state = currentState.copy(handCount = count)
    }

    fun restoreState(state: GameState) {
        this.state = state
    }
    
    /**
     * Synchronize the game state with an authoritative state from the host.
     * This mirrors Android's GameEngine.synchronizeGameState() - it completely replaces
     * the local state with the network state.
     * 
     * @param networkState The authoritative game state from the host
     * @return true if synchronization was successful
     */
    fun synchronizeGameState(networkState: GameState): Boolean {
        try {
            // DEBUG: Log sync
            println("🔄 synchronizeGameState: incoming hand size=${networkState.currentHand.size}, currentPlayerId=${networkState.currentPlayerId}")
            
            DebugLogger.d("GameEngine", "🔄 SYNC: Synchronizing with authoritative host state")
            DebugLogger.d("GameEngine", "🔄 SYNC: Current player: ${state?.currentPlayerId} -> ${networkState.currentPlayerId}")
            DebugLogger.d("GameEngine", "🔄 SYNC: Hand size: ${state?.currentHand?.size} -> ${networkState.currentHand.size}")
            DebugLogger.d("GameEngine", "🔄 SYNC: Moves count: ${state?.moves?.size} -> ${networkState.moves.size}")
            
            // COMPLETE SYNCHRONIZATION: Accept authoritative host state
            val previousState = state
            state = networkState
            
            DebugLogger.d("GameEngine", "✅ SYNC: Complete synchronization with host completed")
            
            // Only notify game start if it's actually a new game or if we were in setup
            if (previousState == null || previousState.gamePhase == GamePhase.SETUP) {
                listener?.onGameStarted(networkState)
            }
            
            return true
        } catch (e: Exception) {
            DebugLogger.d("GameEngine", "❌ SYNC: Failed to synchronize game state: ${e.message}")
            return false
        }
    }
    
    fun getAvailablePins(): List<Pin> {
        return state?.currentHand ?: emptyList()
    }
    
    fun canUseJoker(): Boolean {
        // Simplified logic
        return true
    }
    
    fun syncPlayers(players: List<Player>, localPlayerId: String? = null, localPlayerName: String? = null) {
        val currentState = state ?: return
        // Ideally we should use localPlayerId/Name to ensure consistency but for now just update players
        state = currentState.copy(players = players)
    }
    
    fun endGame() {
        val currentState = state ?: return
        this.state = currentState.copy(started = false)
        listener?.onGameEnded(currentState)
    }
}
